import {useMutation} from '@tanstack/react-query';
import {useCustomerQuery} from 'api/queries';
import {updateCustomer} from 'api/requests';
import {BlockedAccount} from 'components/shared/BlockedAccount';
import {useToast, useTranslation} from 'hooks';
// import useSafeReplaceLanguage from 'hooks/useSafeReplaceLanguage';
import useUtmTelegram from 'hooks/useUtmTelegram';
import {FC, PropsWithChildren, useEffect, useState} from 'react';
import OneSignalReact from 'react-onesignal';
import {setCookie} from 'react-use-cookie';
import {ICustomerResponse, ICustomerUpdateRequest, ICustomerUpdateResponse} from 'types';
import {sendTags} from 'utils/onesignal';

import logger from '../../utils/logger';
import {CustomerContext} from './contexts/CustomerContext';

const UserProvider: FC<PropsWithChildren> = ({children}) => {
  const [showToast, setShowToast] = useState(true);
  const toast = useToast();
  const {t, locale} = useTranslation();
  // const {safeReplace} = useSafeReplaceLanguage();

  const {
    data: customer = {} as ICustomerResponse,
    isFetching: isLoadingGetting,
    refetch: refetchGetCustomer,
  } = useCustomerQuery({
    onSuccess: (customer: ICustomerResponse) => {
      logger.updateUser(customer);

      if (customer.phonenumber) sendTags(customer.phonenumber);
      if (customer.lang_code) setCookie('NEXT_LOCALE', customer.lang_code, {days: 365});
      if (customer.lang_code !== locale) {
        logger.logDebug(`Customer lang !== locale ${customer.lang_code} ${locale}`);
        // safeReplace(customer.lang_code);
      }
    },
  });
  useUtmTelegram(customer, isLoadingGetting);

  useEffect(() => {
    const handleSubscriptionChange = (isSubscribed: boolean) => {
      console.log("The user's subscription state is now:", isSubscribed);
      if (isSubscribed && customer.phonenumber) sendTags(customer.phonenumber);
    };
    OneSignalReact.on('subscriptionChange', handleSubscriptionChange);
    () => {
      OneSignalReact.off('subscriptionChange', handleSubscriptionChange);
    };
  }, [customer.phonenumber]);

  const mutationUpdateCustomer = useMutation<ICustomerUpdateResponse, boolean, ICustomerUpdateResponse, any>(
    updateCustomer,
    {
      onError: () => {
        if (showToast) toast.error(t('ERROR_UPDATE_DATA'));
      },
      onSuccess: () => {
        if (showToast) toast.success(t('SUCCESS_UPDATE_DATA'));
        refetchGetCustomer();
      },
    },
  );

  const updateCustomerData = async (data: ICustomerUpdateRequest, showToastState: boolean) => {
    setShowToast(showToastState);
    if (data) await mutationUpdateCustomer.mutateAsync(data);
  };

  const getCustomer = () => {
    if (isLoadingGetting) return;
    refetchGetCustomer();
  };

  return (
    <CustomerContext.Provider
      value={{
        customer,
        isLoadingGetting,
        isLoadingUpdate: mutationUpdateCustomer.isLoading,
        isLoadingUpdateSuccess: mutationUpdateCustomer.isSuccess,
        updateCustomerData,
        refetchCustomer: getCustomer,
      }}
    >
      {children}
      <BlockedAccount />
    </CustomerContext.Provider>
  );
};

export const CustomerProvider: FC<PropsWithChildren> = ({children}) => {
  return <UserProvider>{children}</UserProvider>;
};
